import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import {
  GetTelegramChannelsResponse,
  TelegramChannelsRowInfo,
} from 'api/types/httpsTypes/contracts';
import Button from 'modules/shared-components/button/button';
import { Link } from 'react-router-dom';
import { MizarHexagonImage } from 'modules/shared-components/hex-logo';
import Big from 'big.js';
import Tooltip from 'modules/shared-components/tooltip';
import Tag from 'modules/shared-components/data-display/tag';
import {
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

/* eslint-disable */
import Chart from 'react-apexcharts';
import { LikesProps, StrategyV2 } from 'api/types/httpsTypes/marketplace';
import { useMemo, useState } from 'react';
import { returnsToPercentage } from 'modules/marketplace/components/strategy-card/strategy-stats';
import {
  chartOptions,
  formatPnl,
} from 'modules/marketplace/components/pnl-chart/pnl-chart-config';
import classNames from 'classnames';
import { formatWithoutDecimals } from 'utils/FormatNumber';
import { useMutation } from '@tanstack/react-query';
import { useSnackBar } from 'modules/layouts/SnackBar/context';
import { MarketplaceService } from 'api/services/httpServices/MarketplaceService';
import { useToggle } from 'modules/utils/useToggle';
import ShareBotModal from 'modules/marketplace-performance-page/components/ShareBotModal';
import { LikingDisabled } from '../../shared/utils/blockFeatures';
import { twMerge } from 'tailwind-merge';

type Props = {
  bots: StrategyV2[];
  pagination: GetTelegramChannelsResponse['pagination'];
  fetchNextPage: () => {};
  isFetching: boolean;
  isMain?: boolean;
  likes: LikesProps | undefined;
};

const MarketplaceCards = ({
  pagination,
  bots,
  fetchNextPage,
  isFetching,
  isMain,
  likes,
}: Props) => {
  const isLargeScreen = useMediaQuery(screenGte.medium);

  return (
    <>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:grid-cols-5 sm:gap-4">
        {bots.map((bot, id) => {
          const isLiked = likes
            ? likes.strategies.some((i) => i === bot.id)
            : false;
          return isLargeScreen ? (
            <MarketplaceCardDesktop isLiked={isLiked} key={id} bot={bot} />
          ) : (
            <MarketplaceCardMobile isLiked={isLiked} key={id} bot={bot} />
          );
        })}
      </div>

      {isMain ? (
        ''
      ) : (
        <div className="flex flex-column text-center justify-center items-center content-center space-y-2">
          <div className="xxs:text-xs text-white-500">
            You&apos;ve viewed {bots.length} of {pagination.total} bots
          </div>
          <div className="w-40 xxs:bg-white-600 dark:bg-black-400 xxs:rounded-sm h-1 xxs:mt-3 -ml-1.5 -mr-0.5">
            <div
              className="h-1 xxs:bg-blue-500 xxs:rounded-sm"
              style={{
                width: `${(bots.length / pagination.total) * 100}%`,
              }}
            />
          </div>

          <div>
            {bots.length < pagination.total && (
              <Button
                onClick={() => {
                  void fetchNextPage();
                }}
                type="button"
                loading={isFetching}
                variant="neutral"
              >
                Show more
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceCards;

const aumRange = (d: number): string => {
  if (d < 0) {
    return '<$0k';
  } else if (d >= 0 && d <= 10000) {
    return '$0-10k';
  } else if (d > 10000 && d <= 100000) {
    return '$10-100k';
  } else if (d > 100000 && d <= 1000000) {
    return '$100k-1M';
  } else if (d > 1000000 && d <= 10000000) {
    return '$1-10M';
  } else {
    return '>10M';
  }
};

const followersRange = (d: number): string => {
  if (d >= 0 && d <= 10) {
    return '0-10';
  } else if (d > 10 && d <= 50) {
    return '10-50';
  } else if (d > 50 && d <= 200) {
    return '50-200';
  } else if (d > 200 && d <= 1000) {
    return '200-1k';
  } else {
    return '>1k';
  }
};

const OtherStats = ({ bot }: { bot: StrategyV2 }) => {
  return (
    <div className="flex justify-between xxs:py-2 xxs:px-1 max-w-[250px]">
      <Tooltip text="Number of Followers">
        <div className="flex space-x-1 items-center text-white-600 cursor-default	">
          <UserGroupIcon className="h-4 w-4" />
          <div>
            {followersRange(Big(bot.numberOfFollowers ?? 0).toNumber())}
          </div>
        </div>
      </Tooltip>
      <Tooltip text="Followers' AUM">
        <div className="flex space-x-1 items-center text-white-600 cursor-default	">
          <BanknotesIcon className="h-4 w-4" />
          <div>{aumRange(Big(bot.aum ?? 0).toNumber())}</div>
        </div>
      </Tooltip>
      <Tooltip text="Followers' Realized PnL">
        <div className="flex space-x-1 items-center text-white-600 cursor-default	">
          <ChartBarIcon className="h-4 w-4" />
          <div>{aumRange(Big(bot.totalPnlFromCopiers ?? 0).toNumber())}</div>
        </div>
      </Tooltip>
    </div>
  );
};

export const MarketplaceCardDesktop = ({
  bot,
  isLiked,
}: {
  bot: StrategyV2;
  isLiked: boolean;
}) => {
  const [isOpenShareModal, { open: openShareModal, close: closeShareModal }] =
    useToggle();
  const pnl = useMemo(
    () =>
      Object.entries(bot.last30DaysReturnsCurve).map(
        ([key, value]) =>
          [new Date(key).valueOf(), returnsToPercentage(value)] as [
            number,
            number
          ]
      ),
    [bot.last30DaysReturnsCurve]
  );

  return (
    <div
      data-testid="strategy"
      className="magic xxs:border xxs:border-solid border-black-500 group bg-transparent bg-strategy-card-dark hover:bg-strategy-card-dark-raised transition-shadow xxs:rounded-lg"
    >
      <StrategyCardHeader isLiked={isLiked} bot={bot} />
      {pnl && <PnlChart pnl={pnl} />}
      <Stats bot={bot} />

      <div className="p-2 space-y-1">
        <Button
          as={Link}
          variant={
            Big(bot.uniqueFollowers ?? 0).eq(
              bot.traderLevel?.maxNumberOfFollowersPerBot ?? 0
            )
              ? 'error'
              : 'primary'
          }
          to={`/dashboard/marketplace/${bot.id}/performance`}
          className="text-white w-full hover:bg-black-400! text-white "
        >
          COPY
        </Button>
        <div className="flex justify-between items-center pt-0.5">
          <LikeComponent
            isLiked={isLiked}
            botId={bot.id}
            numberOfLikes={bot.numberOfLikes ?? 0}
          />
          <Button className="p-0 m-0" onClick={openShareModal}>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 text-white-600 hover:text-blue-500" />
          </Button>
          {isOpenShareModal && (
            <ShareBotModal botId={bot.id} handleClose={closeShareModal} />
          )}
        </div>
      </div>
    </div>
  );
};

const PnlChart = ({ pnl }: { pnl: [number, number][] }) => {
  return (
    <Chart
      options={chartOptions}
      series={[
        {
          name: 'Cumulated Returns',
          data: pnl,
        },
      ]}
      type="area"
      height={100}
    />
  );
};

export const MarketplaceCardMobile = ({
  bot,
  isConsole,
  isLiked,
}: {
  bot: StrategyV2;
  isConsole?: boolean;
  isLiked: boolean;
}) => {
  return (
    <Link
      to={`/dashboard/marketplace/${bot.id}/performance`}
      className="text-white grid grid-cols-3 items-center xxs:border xxs:border-solid border-black-500 group bg-transparent bg-strategy-card-dark hover:bg-strategy-card-dark-raised shadow-lg hover:shadow-lg hover:shadow-base transition-shadow xxs:rounded-lg"
    >
      <div className="col-span-2 space-y-1">
        <StrategyCardHeader isLiked={isLiked} bot={bot} isConsole={isConsole} />
        <div className="px-2 pb-2 w-full space-y-1">
          <GeneralStats bot={bot} />
          {!isConsole && <OtherStats bot={bot} />}
        </div>
      </div>

      <Stats isConsole={isConsole} bot={bot} />
    </Link>
  );
};

export const AlertRiskyChannel = (data: TelegramChannelsRowInfo) => {
  return (data.rugPullRatio && Big(data.rugPullRatio).gt(0.05)) ||
    (data.numberWinsOverLossesAndRugPulls &&
      data.numberWinsOverLossesAndRugPulls < 0) ? (
    <Tooltip
      text={`This trader has been labeled as risky because ${
        data.rugPullRatio && Big(data.rugPullRatio).gt(0.05)
          ? 'its rug-pull ratio is higher than 5%'
          : ''
      }${
        data.rugPullRatio &&
        Big(data.rugPullRatio).gt(0.05) &&
        data.numberWinsOverLossesAndRugPulls &&
        data.numberWinsOverLossesAndRugPulls < 0
          ? ' and '
          : ''
      }${
        data.numberWinsOverLossesAndRugPulls &&
        data.numberWinsOverLossesAndRugPulls < 0
          ? 'its hit ratio is below 50%'
          : ''
      }`}
    >
      <span className="flex">
        <Tag color="red">
          <ExclamationTriangleIcon className="w-5 h-5" />
        </Tag>
      </span>
    </Tooltip>
  ) : (
    ''
  );
};

export const LikeComponent = ({
  botId,
  numberOfLikes,
  isLiked,
}: {
  isLiked: boolean;
  botId: string;
  numberOfLikes: number;
}) => {
  const { addNewMessage } = useSnackBar();
  const [isLikedQuery, setIsLikedQuery] = useState(isLiked);
  const [likes, setLikes] = useState(numberOfLikes);
  const cannotLike = LikingDisabled();

  const { mutate: createLikeMutate } = useMutation({
    mutationFn: MarketplaceService.createLikeStrategy,
    onSuccess: (data) => {
      console.log('data', data);
      setLikes(data.numberOfLikes);
      setIsLikedQuery(data.likedBySelf);
    },
    onError: (data) => {
      addNewMessage({
        type: 'error',
        title: 'Error',
        message: 'An Error Occurred.',
      });
    },
  });

  const { mutate: deleteLikeMutate } = useMutation({
    mutationFn: MarketplaceService.deleteLikeStrategy,
    onSuccess: (data) => {
      setLikes(data.numberOfLikes);
      setIsLikedQuery(data.likedBySelf);
    },
    onError: () => {
      addNewMessage({
        type: 'error',
        title: 'Error',
        message: 'An Error Occurred.',
      });
    },
  });

  return (
    <>
      {!isLikedQuery ? (
        <Button
          type="button"
          className="p-0 "
          onClick={() => {
            cannotLike
              ? addNewMessage({
                  type: 'error',
                  title: 'Upgrade to Giant to Vote',
                  message:
                    'Oops! You need to be a Giant star to vote. Upgrade now and give it another shot!',
                })
              : createLikeMutate(botId);
          }}
        >
          <div className="flex space-x-1 text-white-600 font-bold items-center hover:text-blue-500">
            <HeartIcon className="h-4 w-4 " />
            <div>{formatWithoutDecimals(Big(likes ?? 0).toNumber())}</div>
          </div>
        </Button>
      ) : (
        <Button
          className="p-0"
          type="button"
          onClick={() => {
            cannotLike
              ? addNewMessage({
                  type: 'error',
                  title: 'Upgrade to Giant to Vote',
                  message:
                    'Oops! You need to be a Giant star to vote. Upgrade now and give it another shot!',
                })
              : deleteLikeMutate(botId);
          }}
        >
          <div className="flex space-x-1 text-white-600 font-bold items-center hover:text-blue-500">
            <HeartIconSolid className="h-4 w-4 text-blue-500" />
            <div>{formatWithoutDecimals(Big(likes ?? 0).toNumber())}</div>
          </div>
        </Button>
      )}
    </>
  );
};

export const TraderLevel = ({ level }: { level: string }) => {
  return level ? (
    <a
      href="https://docs.mizar.com/c-mizar-manual/copy-trading/trader-levels"
      rel="noreferrer"
      className="text-white"
      target="_blank"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip
        text={`This trader has a ${level.toLowerCase()} level. Click to learn more about trader levels.`}
      >
        <img
          alt={`trader-level-${level.toLowerCase()}`}
          className="h-5 w-5"
          src={require(`assets/img/trader-levels/${level.toLowerCase()}.png`)}
        />
      </Tooltip>
    </a>
  ) : (
    <></>
  );
};
const StrategyCardHeader = ({
  bot,
  isConsole,
  isLiked,
}: {
  bot: StrategyV2;
  isConsole?: boolean;
  isLiked: boolean;
}) => {
  return (
    <div
      className={classNames(
        '  xxs:px-2 xxs:py-2 rounded-t-lg space-y-1',
        !isConsole ? 'md:bg-black-700 md:group-hover:bg-transparent' : ''
      )}
    >
      <div className="xxs:flex items-center xxs:gap-1">
        <MizarHexagonImage size={50} alt="Trader logo" src={bot.imgUrl} />

        <div className="w-full">
          <div className="flex space-x-2 items-center ">
            <div className="max-w-[160px] md:max-w-[120px]">
              <Link
                to={`/dashboard/marketplace/${bot.id}/performance`}
                className="xxs:font-semibold text-white text-sm"
              >
                <Tooltip text={bot.strategyName}>
                  <div className="truncate">{bot.strategyName}</div>
                </Tooltip>
              </Link>
            </div>
            <div
              className={twMerge(
                'xxs:flex  items-center',
                isConsole ? '' : 'lg:hidden'
              )}
            >
              <LikeComponent
                isLiked={isLiked}
                botId={bot.id}
                numberOfLikes={bot.numberOfLikes ?? 0}
              />
            </div>
          </div>
          <div className="flex space-x-1 items-center">
            <div>by {bot.traderName}</div>
            <TraderLevel level={bot.traderLevel?.name ?? ''} />
          </div>
        </div>
      </div>
      <div className="hidden lg:block">
        <OtherStats bot={bot} />
      </div>
    </div>
  );
};

const GeneralStats = ({ bot }: { bot: StrategyV2 }) => {
  return (
    <div className="flex space-x-1 text-xs">
      <Tag condensed color="neutral">
        <div className="uppercase text-xs">
          {bot.side.toLowerCase() === 'long/short'
            ? 'L/S'
            : bot.side.toLowerCase() === 'long'
            ? 'LONG'
            : 'SHORT'}
        </div>
      </Tag>
      <Tag condensed color="neutral">
        <div className="uppercase text-xs">{bot.market}</div>
      </Tag>
    </div>
  );
};

const Stats = ({
  bot,
  isConsole,
}: {
  bot: StrategyV2;
  isConsole?: boolean;
}) => {
  return (
    <div className="space-y-1 px-2">
      <div className="md:flex xxs:gap-x-2 text-center  md:items-end">
        <div
          className={`xxs:text-xl ${
            !bot.last30DaysReturns
              ? ''
              : Big(bot.last30DaysReturns ?? 0).gte(0)
              ? 'xxs:text-green-success'
              : 'xxs:text-red-warning'
          }`}
        >
          <>{formatPnl(returnsToPercentage(bot.last30DaysReturns))}</>
        </div>
        <div className="xxs:text-xs md:mb-1">30D Returns</div>
      </div>
      {!isConsole && (
        <div className="hidden md:block">
          <GeneralStats bot={bot} />
        </div>
      )}
    </div>
  );
};
